import { useState } from "react";
import type { SubtitleVersion } from "@/mocks/subtitles";

interface SubtitlePreviewProps {
  versions: SubtitleVersion[];
  title: string;
}

export default function SubtitlePreview({ versions, title }: SubtitlePreviewProps) {
  const [activeId, setActiveId] = useState(versions[0]?.id ?? "");
  const [copied, setCopied] = useState(false);
  const active = versions.find((item) => item.id === activeId) ?? versions[0];

  const handleCopy = async () => {
    if (!active) return;
    try {
      await navigator.clipboard.writeText(active.lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!active) {
    return (
      <div className="rounded-lg border border-dashed border-background-300 bg-background-100 p-8 text-center text-sm text-foreground-500">
        资源暂不可用，请稍后再试
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 p-4">
        <div className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <i className="ri-file-text-line text-lg text-primary-500"></i>
          字幕预览
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
        >
          <i className={copied ? "ri-check-line text-secondary-600" : "ri-file-copy-line"}></i>
          {copied ? "已复制字幕文本" : "复制字幕文本"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-background-200 p-4">
        {versions.map((version) => (
          <button
            key={version.id}
            type="button"
            onClick={() => setActiveId(version.id)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors ${
              version.id === activeId
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            <span className="rounded bg-background-50/25 px-1.5 py-0.5 text-[10px] uppercase">
              {version.format}
            </span>
            {version.label}
          </button>
        ))}
      </div>

      <div className="bg-foreground-950 p-4 md:p-5">
        <div className="max-h-[320px] space-y-2.5 overflow-y-auto font-mono text-[13px] leading-relaxed text-background-100">
          {active.lines.map((line, index) => (
            <p key={`${active.id}-${index}`} className="break-words">
              {line}
            </p>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 p-4 text-xs text-foreground-500">
        <span className="flex items-center gap-1">
          <i className="ri-translate-2 text-sm text-primary-500"></i>
          {active.language}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-hard-drive-2-line text-sm text-primary-500"></i>
          {active.size}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-information-line text-sm text-primary-500"></i>
          以上为静态演示字幕片段，仅供预览参考
        </span>
      </div>
    </div>
  );
}